import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addMonths, format as formatDate, startOfMonth } from 'date-fns'

import { newsAudienceFull, newsAudienceShort } from '../../../../../../shared/newsAudience'

import { toWallClock } from '../../../../../../shared/datetime'
import styles from './style.module.scss'

/*
 * Nachrichten auf einer Zeitachse.
 *
 * Die Liste beantwortet "welche Nachrichten gibt es", nicht "was laeuft wann".
 * Genau das ist aber die Frage bei Veranstaltungen: Ueberschneiden sich zwei
 * Ankuendigungen? Klafft im Herbst eine Luecke? Dafuer ein Balken je Nachricht
 * ueber ihren Zeitraum, heute als Linie in der Mitte.
 *
 * Die Skala zeigt immer die halbe Spanne zurueck und die halbe nach vorn —
 * "heute in der Mitte" ist der Bezugspunkt, von dem aus man liest.
 *
 * Jede Beschriftung beginnt am Anfang ihres Balkens und laeuft nach rechts
 * weiter, auch ueber das Balkenende hinaus. Damit steht der Text immer dort, wo
 * der Zeitraum beginnt — frueher sass er mal im Balken, mal dahinter, und die
 * Spalte der Titel war ein Zickzack. Weil Text ueber wie neben dem Balken
 * lesbar bleiben muss, sind die Balken hell gefuellt und tragen ihre Farbe als
 * kraeftige Kante; unterschieden werden die beiden Arten ohnehin am Symbol.
 */

// Veranstaltung: Kalender. Neuigkeit: Megafon. Beide als Strichzeichnung in
// currentColor — so nehmen sie die Farbe der Zeile an, auch wenn die Nachricht
// abgelaufen ist und zurueckgenommen dargestellt wird.
function EventIcon(props) {
	return (
		<svg
			className={props.className}
			viewBox="0 0 16 16"
			width="13"
			height="13"
			aria-hidden="true"
			focusable="false"
		>
			<rect
				x="1.6"
				y="3.1"
				width="12.8"
				height="11.3"
				rx="2"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
			/>
			<path d="M1.6 6.9h12.8" stroke="currentColor" strokeWidth="1.5" />
			<path d="M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
		</svg>
	)
}

function NewsIcon(props) {
	return (
		<svg
			className={props.className}
			viewBox="0 0 16 16"
			width="13"
			height="13"
			aria-hidden="true"
			focusable="false"
		>
			<path
				d="M13.4 2.4v11.2L6.2 10.9H3.6A1.7 1.7 0 0 1 1.9 9.2V6.8a1.7 1.7 0 0 1 1.7-1.7h2.6l7.2-2.7Z"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinejoin="round"
			/>
			<path
				d="M5.3 11v2.2a1.3 1.3 0 0 0 2.6 0v-1.3"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
		</svg>
	)
}

/*
 * Der Pfeil fuer einen Termin, der jenseits des gezeigten Zeitraums liegt.
 * Zwei Winkel statt einer Raute: Die Raute steht fuer einen Tag auf der Achse,
 * dieses Zeichen fuer eine Richtung.
 */
function OutsideIcon(props) {
	return (
		<svg
			className={props.className}
			viewBox="0 0 16 16"
			width="13"
			height="13"
			aria-hidden="true"
			focusable="false"
		>
			<path
				d="M4 3.2 8.4 8 4 12.8M9 3.2 13.4 8 9 12.8"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.9"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

const TypIcon = ({ typ, className }) =>
	typ === 'EVENT' ? <EventIcon className={className} /> : <NewsIcon className={className} />

const SPANNEN = [2, 6, 9, 12]
const STANDARD_SPANNE = 6

// Ohne Enddatum braucht der Balken trotzdem eine Laenge, sonst ist er
// unsichtbar. Zwei Wochen sind lang genug zum Treffen und kurz genug, um nicht
// als echter Zeitraum missverstanden zu werden.
const TAGE_OHNE_ENDE = 14
const MS_PRO_TAG = 24 * 60 * 60 * 1000

/*
 * Die Achse rechnet durchgehend in Wiener Wanduhrzeit — Zeitpunkte werden dazu
 * einmal am Rand verschoben (toWallClock) und danach nur noch untereinander
 * verglichen.
 *
 * Grund sind die Monatsgrenzen: startOfMonth und addMonths kennen nur die Zone
 * des Browsers. Auf einem Rechner, der vor Wien liegt, faellt der Monatsanfang
 * eine Stunde vor die Wiener Grenze — die Beschriftung zeigt dann den Vormonat.
 * Da alle Werte derselben Verschiebung unterliegen, bleiben Abstaende und
 * Anteile unveraendert.
 */
const zuDatum = toWallClock

export default function NewsTimeline(props) {
	const { t } = useTranslation()
	const [spanne, setSpanne] = useState(STANDARD_SPANNE)

	const heute = useMemo(() => toWallClock(new Date()), [])

	// Fenster: halbe Spanne zurueck, halbe nach vorn, auf Monatsgrenzen
	// gerundet — sonst beginnt die Achse an einem willkuerlichen Tag.
	const { von, bis, monate } = useMemo(() => {
		const haelfte = spanne / 2
		const start = startOfMonth(addMonths(heute, -Math.ceil(haelfte)))
		const ende = startOfMonth(addMonths(heute, Math.ceil(haelfte) + 1))

		const spalten = []
		for (let m = new Date(start); m < ende; m = addMonths(m, 1)) spalten.push(new Date(m))

		return { von: start, bis: ende, monate: spalten }
	}, [heute, spanne])

	const gesamt = bis - von
	const anteil = zeitpunkt => ((zeitpunkt - von) / gesamt) * 100

	const balken = useMemo(
		() =>
			props.items
				.map(item => {
					const start = zuDatum(item.startDate)
					if (!start) return null

					const ende = zuDatum(item.endDate) || new Date(start.getTime() + TAGE_OHNE_ENDE * MS_PRO_TAG)

					// Alles ausserhalb des Fensters weglassen statt an den Rand
					// zu quetschen — ein Balken am Anschlag suggeriert eine
					// Dauer, die es nicht gibt.
					if (ende < von || start > bis) return null

					const links = Math.max(0, anteil(start))
					const rechts = Math.min(100, anteil(ende))

					/*
					 * Der Balken ist der Anzeigezeitraum — wie lange angekuendigt
					 * wird. Wann die Veranstaltung stattfindet, ist ein Punkt darin
					 * (oder auch daneben, wenn die Ankuendigung vorher auslaeuft).
					 * Ohne diese Marke beantwortet die Zeitachse "was laeuft wann",
					 * aber nicht "wann ist es".
					 */
					const termin = zuDatum(item.eventStart)
					const terminAnteil = termin && termin >= von && termin <= bis ? anteil(termin) : null

					/*
					 * Ein Termin ausserhalb des Fensters fiel bis 26.08.2026
					 * stillschweigend weg. Man sah eine Veranstaltung ohne Raute
					 * und konnte nicht unterscheiden, ob keiner gepflegt ist oder
					 * ob er nur weiter draussen liegt — bei "Junior Handelsmesse
					 * 2027" (angezeigt Aug/Sep 2026, Termin 23.09.2027) genau der
					 * Fall. An den Rand geschoben waere die Raute eine Luege, denn
					 * sie behauptet einen Tag; deshalb steht dort ein Pfeil, der
					 * die Richtung zeigt und das Datum im Hinweis traegt.
					 */
					const terminDraussen = termin && terminAnteil === null ? (termin < von ? 'vor' : 'nach') : null

					return {
						item,
						links,
						termin,
						terminAnteil,
						terminDraussen,
						breite: Math.max(rechts - links, 0.6),
						// Angeschnitten heisst: reicht ueber das Fenster hinaus.
						vorher: start < von,
						nachher: ende > bis
					}
				})
				.filter(Boolean)
				.sort((a, b) => a.links - b.links),
		/* eslint-disable-next-line */
		[props.items, von, bis]
	)

	const heuteAnteil = anteil(heute)

	return (
		<div className={styles.root}>
			<div className={styles.toolbar}>
				<div className={styles.left}>
					<p className={styles.counter}>
						{balken.length} {t('of')} {props.gesamt ?? props.items.length} {t('Messages')}
					</p>
					<span className={styles.legend}>
						<span className={clsx(styles.legendItem, styles.legendEvent)}>
							<EventIcon className={styles.labelIcon} />
							{t('EVENT')}
						</span>
						<span className={clsx(styles.legendItem, styles.legendNews)}>
							<NewsIcon className={styles.labelIcon} />
							{t('NEWS')}
						</span>
						<span className={clsx(styles.legendItem, styles.legendEvent)}>
							<span className={styles.legendDiamond} aria-hidden="true" />
							{t('Event date')}
						</span>
						{/*
						 * Der Entwurf gehoert in die Legende, weil er als einziger nichts
						 * ueber die Art sagt: Ein dunkler Balken ist ein Entwurf, ob
						 * Veranstaltung oder Neuigkeit. Ohne die Zeile bleibt offen,
						 * wofuer die dritte Farbe steht.
						 */}
						<span className={styles.legendItem}>
							<span className={styles.legendDraftBar} aria-hidden="true" />
							{t('Draft')}
						</span>
					</span>
				</div>
				<div className={styles.spans}>
					<span className={styles.spansLabel}>{t('Period shown')}</span>
					{SPANNEN.map(monat => (
						<button
							type="button"
							key={monat}
							className={clsx(styles.span, monat === spanne && styles.spanActive)}
							onClick={() => setSpanne(monat)}
						>
							{monat} {t('months')}
						</button>
					))}
				</div>
			</div>

			{/*
			 * Die Balkenlaenge ist die Anzeigedauer, nicht der Termin — beides
			 * steht an einer Nachricht und wird sonst verwechselt. Der Satz sagt
			 * es einmal aus, damit niemand aus der Balkenlaenge auf die Dauer
			 * einer Veranstaltung schliesst.
			 */}
			<p className={styles.hint}>{t('timelineBarHint')}</p>

			<div className={styles.chart}>
				<div className={styles.axis}>
					{monate.map(monat => (
						<div
							key={monat.toISOString()}
							className={styles.axisMonth}
							style={{
								left: `${anteil(monat)}%`,
								width: `${anteil(addMonths(monat, 1)) - anteil(monat)}%`
							}}
						>
							<span className={styles.axisLabel}>{formatDate(monat, 'MMM RRRR')}</span>
						</div>
					))}
				</div>

				<div className={styles.lanes}>
					{/* Raster zuerst, damit die Balken darueber liegen. */}
					{monate.map(monat => (
						<div key={monat.toISOString()} className={styles.grid} style={{ left: `${anteil(monat)}%` }} />
					))}

					<div className={styles.today} style={{ left: `${heuteAnteil}%` }}>
						<span className={styles.todayLabel}>{t('today')}</span>
					</div>

					{balken.length === 0 ? (
						<p className={styles.empty}>{t('No messages in this period.')}</p>
					) : (
						balken.map(({ item, links, breite, vorher, nachher, termin, terminAnteil, terminDraussen }) => {
							/*
							 * Die Zeile beginnt am Balkenanfang und reicht bis zum
							 * rechten Rand. So ist der Balken ihr erstes Stueck und
							 * die Beschriftung darf darueber hinauslaufen, ohne
							 * eigene Rechnerei — und der ueberstehende Text wird am
							 * Diagrammrand sauber abgeschnitten statt herauszuragen.
							 */
							const rest = Math.max(100 - links, 0.6)
							const balkenAnteil = Math.min((breite / rest) * 100, 100)

							// Die Zielgruppe: kurz in der Klammer hinter dem Titel,
							// ausgeschrieben im Hinweis beim Darueberfahren.
							const facets = props.audienceOf?.(item)
							const zielgruppe = facets && newsAudienceShort(facets, t)
							const zielgruppeLang = facets && newsAudienceFull(facets, t)

							return (
								<div key={item.id} className={styles.lane}>
									{/*
									 * Der Termin als Raute — ein Meilenstein, kein
									 * Zeitraum. Sie sitzt in der Spur und nicht im
									 * Zeilenknopf, weil die Zeile erst beim Balken
									 * beginnt: ein Termin vor dem Anzeigebeginn fiele
									 * dort unter den Tisch. So liegt die Raute immer an
									 * ihrem Tag auf der Achse.
									 */}
									{terminAnteil !== null && (
										<button
											type="button"
											className={clsx(
												styles.eventMark,
												item.status === 'Expired' && styles.eventMarkExpired
											)}
											style={{ left: `${terminAnteil}%` }}
											onClick={() => props.onOpen(item)}
											title={`${t('Event date')}: ${formatDate(termin, 'dd.MM.yyyy')}`}
											aria-label={`${item.title} — ${t('Event date')}: ${formatDate(
												termin,
												'dd.MM.yyyy'
											)}`}
										/>
									)}
									{!!terminDraussen && (
										<button
											type="button"
											className={clsx(
												styles.eventMarkOutside,
												terminDraussen === 'vor' && styles.eventMarkOutsideStart,
												item.status === 'Expired' && styles.eventMarkOutsideExpired
											)}
											onClick={() => props.onOpen(item)}
											title={`${t('Event date')}: ${formatDate(termin, 'dd.MM.yyyy')} — ${t(
												'outside the period shown'
											)}`}
											aria-label={`${item.title} — ${t('Event date')}: ${formatDate(
												termin,
												'dd.MM.yyyy'
											)} — ${t('outside the period shown')}`}
										>
											<OutsideIcon className={styles.eventMarkOutsideIcon} />
										</button>
									)}
									<button
										type="button"
										className={clsx(
											styles.entry,
											item.newsType === 'EVENT' ? styles.entryEvent : styles.entryNews,
											item.status === 'Expired' && styles.entryExpired,
											item.status === 'Draft' && styles.entryDraft
										)}
										style={{ left: `${links}%`, width: `${rest}%` }}
										onClick={() => props.onOpen(item)}
										title={
											zielgruppeLang
												? `${item.title} — ${t('Target group')}: ${zielgruppeLang}`
												: item.title
										}
									>
										<span
											className={clsx(
												styles.bar,
												vorher && styles.barCutStart,
												nachher && styles.barCutEnd
											)}
											style={{ width: `${balkenAnteil}%` }}
										/>
										<span className={styles.label}>
											<TypIcon typ={item.newsType} className={styles.labelIcon} />
											{/*
											 * Entwurf und Abgelaufenes sind beide grau — das
											 * Wort haelt sie auseinander (#164).
											 */}
											{item.status === 'Draft' && (
												<span className={styles.labelDraft}>{t('Draft')}</span>
											)}
											<span className={styles.labelText}>{item.title}</span>
											{/*
											 * Ohne diese Klammer sehen zwei Balken gleich
											 * aus, von denen der eine an alle geht und der
											 * andere nur an die Lehrkraefte eines
											 * Bundeslandes — beim Planen ist das der
											 * Unterschied, auf den es ankommt.
											 */}
											{!!zielgruppe && (
												<span className={styles.labelAudience}>({zielgruppe})</span>
											)}
										</span>
									</button>
								</div>
							)
						})
					)}
				</div>
			</div>
		</div>
	)
}

NewsTimeline.propTypes = {
	items: PropTypes.array.isRequired,
	// Gesamtbestand vor dem Filtern — der Zaehler soll "3 von 25" sagen koennen
	// und nicht "3 von 3", sobald ein Filter greift.
	gesamt: PropTypes.number,
	onOpen: PropTypes.func.isRequired,

	// Zielgruppe einer Nachricht (`shared/newsAudience`). Fehlt die Angabe,
	// bleibt die Klammer hinter dem Titel weg.
	audienceOf: PropTypes.func
}
